import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ShieldCheck, ShieldAlert, Loader2, BookOpen, Users, Calendar, GraduationCap } from "lucide-react";
import { API_BASE_URL } from "../services/api";

interface VerificationResult {
  valid: boolean;
  message?: string;
  scheme_id?: number;
  school_name?: string | null;
  subject_name?: string | null;
  subject_code?: string | null;
  class_group_name?: string | null;
  term_name?: string | null;
  year_name?: string | null;
  teacher_name?: string | null;
  validation_status?: "PENDING" | "APPROVED" | "REJECTED";
  created_at?: string;
}

/** Public, unauthenticated page a QR code printed on every Scheme of Work PDF links to (see
 * schemeReportPdf.ts) -- anyone holding a printed copy can scan it to confirm the document was
 * genuinely produced by this system, without needing to log in. Deliberately outside
 * ProtectedRoute/PublicRoute in App.tsx: it must render the same for a logged-in staff member and
 * an anonymous visitor. */
const SchemeVerifyPage: React.FC = () => {
  const { schemeId } = useParams<{ schemeId: string }>();
  const [result, setResult] = useState<VerificationResult | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/scheme-of-work/verify/${schemeId}`);
        const data = await res.json();
        if (!cancelled) setResult(data);
      } catch {
        if (!cancelled) setResult({ valid: false, message: "Could not reach the verification service" });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [schemeId]);

  const statusBadge = (status?: string) => {
    if (status === "APPROVED")
      return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700">Approved</span>;
    if (status === "REJECTED")
      return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-rose-100 text-rose-700">Rejected</span>;
    return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-700">Pending Review</span>;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            <p className="text-sm text-slate-500">Checking document…</p>
          </div>
        ) : result?.valid ? (
          <>
            <div className="bg-gradient-to-br from-emerald-500 to-teal-600 px-6 py-8 text-center text-white">
              <div className="w-14 h-14 mx-auto rounded-2xl bg-white/20 flex items-center justify-center mb-3">
                <ShieldCheck className="w-8 h-8" />
              </div>
              <h1 className="text-lg font-bold">Document Verified</h1>
              <p className="text-sm text-white/80 mt-1">
                This Scheme of Work was genuinely generated by {result.school_name || "NGA Central MIS"}.
              </p>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-start gap-3">
                <BookOpen className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-slate-400">Subject</p>
                  <p className="text-sm font-semibold text-slate-800">
                    {result.subject_code ? `${result.subject_code}: ` : ""}
                    {result.subject_name || "N/A"}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Users className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-slate-400">Class &amp; Teacher</p>
                  <p className="text-sm font-semibold text-slate-800">
                    {result.class_group_name || "N/A"} — {result.teacher_name || "N/A"}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Calendar className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-slate-400">Term &amp; Academic Year</p>
                  <p className="text-sm font-semibold text-slate-800">
                    {result.term_name || "N/A"} · {result.year_name || "N/A"}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <GraduationCap className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-slate-400">Validation Status</p>
                  <div className="mt-1">{statusBadge(result.validation_status)}</div>
                </div>
              </div>
              <p className="text-[11px] text-slate-300 text-center pt-2 border-t border-slate-100">
                Document reference #{result.scheme_id}
              </p>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 px-6 text-center gap-3">
            <div className="w-14 h-14 rounded-2xl bg-rose-50 flex items-center justify-center">
              <ShieldAlert className="w-8 h-8 text-rose-500" />
            </div>
            <h1 className="text-lg font-bold text-slate-800">Not Verified</h1>
            <p className="text-sm text-slate-500">
              {result?.message || "We couldn't find a matching document for this code."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SchemeVerifyPage;
